// @ts-check
/// <reference path="../../../state/rdms/types.js" />
/// <reference path="../../../state/userStats/types.js" />

import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  handleAddRdmUser,
  handleGetRdm,
  handleRemoveRdmUser,
  handleUpdateRdmUser,
} from "../../../state/rdms/actions";
import { toastAlert } from "../../../components/Toastify";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "../../RDM/viewModel/useForm";
import { useActions } from "./useActions";
import { useFormUser } from "./useFormUser";
import { handleListTaskAccountable } from "../../../state/users/actions";

/**
 * @param {string} rdmId
 * @returns {{
 *  userPermissions: {
 *    canEdit: boolean,
 *    canDelete: boolean,
 *    canAdd: boolean
 * },
 *  currentUser: RdmUser | null,
 *  openModal: boolean,
 *  openRemoveModal: boolean,
 *  openUserModal: boolean,
 *  openUserRemoveModal: boolean,
 *  isEditUser: boolean,
 *  onCloseModal: () => void,
 *  onCloseModalUser: () => void,
 *  onCloseRemoveUserModal: () => void,
 *  onDelete: () => void,
 *  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void,
 *  handleSubmitUser: (e: React.FormEvent<HTMLFormElement>, edit?: boolean) => void,
 *  handleOpenUserModal: (rdmUser?: RdmUser) => void,
 *  handleOpenRemoveModal: (rdmUser?: RdmUser) => void,
 *  handleRemoveUser: () => void,
 *  data: Rdm|null,
 *  title: string,
 *  loading: boolean,
 *  users: any[],
 *  handleCollapse: (id: string) => void,
 *  collapses: Object<string, boolean>,
 *  toDisableModal: () => boolean,
 *  toDisableModalUser: () => boolean,
 *  form: { title: { value: string, onChange: (value: string) => void },
 *          description: { value: string, onChange: (value: string) => void } },
 *  formUser: { user: { value: User | undefined, onChange: (value: User) => void },
 *            positives: { value: number, onChange: (value: number) => void },
 *            negatives: { value: number, onChange: (value: number) => void },
 *            description: { value: string, onChange: (value: string) => void } },
 *  options: { rdm: { label: string, onClick: () => void, show: boolean }[],
 *             users: { label: string, onClick: (rdmUser?: RdmUser) => void, show: boolean }[] }
 * }}
 */
export function usePanel(rdmId) {
  /** @type {[Rdm|null, Function]} */
  const [data, setData] = useState(/** @type {Rdm|null} */ (null));

  /** @type {[RdmUser|null, (user: RdmUser|null) => void]} */
  const [currentUser, setCurrentUser] = useState(
    /** @type {RdmUser|null} */ (null)
  );

  const [loading, setLoading] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [openRemoveModal, setRemoveModal] = useState(false);
  const [openUserModal, setOpenUserModal] = useState(false);
  const [openUserRemoveModal, setOpenUserRemoveModal] = useState(false);

  const [users, setUsers] = useState([]);

  const history = useHistory();
  const dispatch = useDispatch();
  const { handleUpdateFormValues, form, title, description } = useForm();
  const { formUser, handleUpdateFormValues: handleUpdateFormValuesUser } =
    useFormUser();

  const { onDelete, onUpdate } = useActions(
    data?._id,
    () => setRemoveModal(false),
    () => history.push("/rdm")
  );

  // @ts-ignore
  const user = useSelector((state) => state.auth.user);

  const [collapses, setCollapses] = useState({
    "rdm-card": true,
    "users-card": true,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await handleGetRdm(rdmId);
      setData(res);
      handleUpdateFormValues({
        title: res.title,
        description: res.description,
      });
    } catch (error) {
      toastAlert({
        msg: `Erro ao carregar Rdm`,
        isError: true,
      });
      history.push("/rdm");
    } finally {
      setLoading(false);
    }
  }, [handleUpdateFormValues, history, rdmId]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await dispatch(handleListTaskAccountable());
      // @ts-ignore
      setUsers(res.data.usersByTenant);
    } catch (err) {
      toastAlert({
        msg: `Ocorreu um erro ao buscar usuários disponiveis: ${err}`,
        isError: true,
      });
    }
  }, [dispatch]);

  /**
   * @param {string} id
   */
  const handleCollapse = (id) => {
    setCollapses({
      ...collapses,
      [id]: !collapses[id],
    });
  };

  /**
   * @param {React.FormEvent<HTMLFormElement>} e
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (confirmSubmit) return;
    setConfirmSubmit(true);
    try {
      const response = await onUpdate(title, description);
      handleUpdateDataFields(response);
      setOpenModal(false);
    } catch (error) {
      toastAlert({
        msg: `Ocorreu um erro ao atualizar o Rdm`,
        isError: true,
      });
    } finally {
      setConfirmSubmit(false);
    }
  };

  /**
   * @param {React.FormEvent<HTMLFormElement>} e
   */
  const handleSubmitUser = async (e) => {
    e.preventDefault();
    setOpenUserModal(false);

    const userData = formUser.user.value;

    if (confirmSubmit || !data || !userData) return;
    setConfirmSubmit(true);
    const rdmUserdata = {
      userId: userData?._id,
      positives: formUser.positives.value,
      negatives: formUser.negatives.value,
      description: formUser.description.value,
    };
    try {
      if (currentUser) {
        const rdmUser = data.rdmUsers.find(
          (rdmUser) => rdmUser.userId === userData._id
        );
        if (!rdmUser) return;

        /** @type {RdmUser}  */
        const res = await handleUpdateRdmUser(rdmUser._id, rdmUserdata);
        toastAlert({
          msg: `Usuário atualizado com sucesso!`,
        });
        handleSetUpdateUser(res, userData);
      } else {
        /** @type {RdmUser}  */
        const res = await handleAddRdmUser(data?._id, rdmUserdata);
        toastAlert({
          msg: `Usuário criado com sucesso!`,
        });
        handleSetNewUser(res, userData);
      }
    } catch (error) {
      toastAlert({
        msg: `Ocorreu um erro ao salvar o usuário`,
        isError: true,
      });
    } finally {
      setConfirmSubmit(false);
      resetUserForm();
    }
  };

  const handleRemoveUser = async () => {
    try {
      if (!currentUser) return;
      if (confirmSubmit) return;
      setConfirmSubmit(true);
      await handleRemoveRdmUser(currentUser._id);
      handleSetRemoveUser(currentUser);
      toastAlert({
        msg: `Usuário deletado com sucesso!`,
      });
    } catch (error) {
      toastAlert({
        msg: `Ocorreu um erro ao deletar o usuário`,
        isError: true,
      });
    } finally {
      onCloseRemoveUserModal();
      setRemoveModal(false);
      setConfirmSubmit(false);
    }
  };

  /**
   * @param {RdmUser} rdmUser
   * @param {User} userData
   */
  const handleSetNewUser = (rdmUser, userData) => {
    const newRdmUser = {
      ...rdmUser,
      userData: {
        ...userData,
      },
    };
    setData((prevState) => {
      return {
        ...prevState,
        rdmUsers: [...(prevState.rdmUsers || []), newRdmUser],
      };
    });
  };

  /**
   * @param {RdmUser} rdmUser
   * @param {User} userData
   */
  const handleSetUpdateUser = (rdmUser, userData) => {
    const updateRdmUser = {
      ...rdmUser,
      userData: {
        ...userData,
      },
    };
    setData((prevState) => {
      return {
        ...prevState,
        rdmUsers: prevState.rdmUsers.map((user) => {
          if (user._id === rdmUser._id) {
            return updateRdmUser;
          }
          return user;
        }),
      };
    });
  };

  const handleSetRemoveUser = (rdmUser) => {
    if (!rdmUser) return;

    setData((prevState) => {
      return {
        ...prevState,
        rdmUsers: prevState.rdmUsers.filter((user) => user._id !== rdmUser._id),
      };
    });
  };

  const resetUserForm = () => {
    setCurrentUser(null);
    handleUpdateFormValuesUser({
      user: "",
      positives: 0,
      negatives: 0,
      description: "",
    });
  };

  const handleUpdateDataFields = (data) => {
    setData((prevState) => ({
      ...prevState,
      title: data.title,
      description: data.description,
    }));
    handleUpdateFormValues({
      title: data?.title,
      description: data?.description,
    });
  };

  const userPermissions = useMemo(() => {
    return {
      canEdit: user.rank === "admin" || user.rank === "gerente",
      canDelete: user.rank === "admin" || user.rank === "gerente",
      canAdd: user.rank === "admin" || user.rank === "gerente",
    };
  }, [user]);

  const handleOpenUserModal = useCallback(
    /**
     * @param {RdmUser | undefined} rdmUser
     */
    (rdmUser) => {
      setOpenUserModal(true);
      if (rdmUser) {
        handleUpdateFormValuesUser({
          user: rdmUser.userData,
          positives: rdmUser.positives,
          negatives: rdmUser.negatives,
          description: rdmUser.description,
        });
        setCurrentUser(rdmUser);
      }
    },
    [handleUpdateFormValuesUser]
  );

  const handleOpenRemoveModal = useCallback(
    /**
     * @param {RdmUser | undefined} rdmUser
     */
    (rdmUser) => {
      if (!rdmUser) return;
      setOpenUserRemoveModal(true);
      setCurrentUser(rdmUser);
    },
    []
  );

  const options = useMemo(() => {
    return {
      rdm: [
        {
          label: "Editar",
          onClick: () => setOpenModal(true),
          show: userPermissions.canEdit,
        },
        {
          label: "Excluir",
          onClick: () => setRemoveModal(true),
          show: userPermissions.canDelete,
        },
      ],
      users: [
        {
          label: "Editar",
          /**  @type {(rdmUser: RdmUser | undefined) => void} */
          onClick: (rdmUser) => handleOpenUserModal(rdmUser),
          show: userPermissions.canEdit,
        },
        {
          label: "Excluir",
          onClick: (rdmUser) => handleOpenRemoveModal(rdmUser),
          show: userPermissions.canDelete,
        },
      ],
    };
  }, [
    handleOpenRemoveModal,
    handleOpenUserModal,
    userPermissions.canDelete,
    userPermissions.canEdit,
  ]);

  const onCloseModal = () => {
    setRemoveModal(false);
    setOpenModal(false);
    handleUpdateFormValues({
      title: data?.title,
      description: data?.description,
    });
  };

  const onCloseModalUser = () => {
    setOpenUserModal(false);
    handleUpdateFormValuesUser({
      user: "",
      positives: 0,
      negatives: 0,
      description: "",
    });
    setOpenUserRemoveModal(false);
    setCurrentUser(null);
  };

  const onCloseRemoveUserModal = () => {
    setOpenUserRemoveModal(false);
    setCurrentUser(null);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchUsers();
  }, [dispatch, fetchUsers]);

  const toDisableModal = useCallback(() => {
    return (
      form.title.value === data?.title &&
      form.description.value === data?.description
    );
  }, [
    data?.description,
    data?.title,
    form.description.value,
    form.title.value,
  ]);

  const toDisableModalUser = useCallback(() => {
    return (
      formUser.user.value === undefined ||
      formUser.description.value === "" ||
      !!(
        currentUser &&
        currentUser.positives === formUser.positives.value &&
        currentUser.negatives === formUser.negatives.value &&
        currentUser.description.trim() === formUser.description.value.trim()
      )
    );
  }, [
    formUser.description.value,
    formUser.negatives.value,
    formUser.positives.value,
    formUser.user.value,
    currentUser,
  ]);

  return {
    currentUser,
    userPermissions,
    openModal,
    openRemoveModal,
    openUserModal,
    openUserRemoveModal,
    onCloseRemoveUserModal,
    form,
    formUser,
    title,
    onCloseModal,
    onCloseModalUser,
    onDelete,
    data,
    loading,
    users,
    collapses,
    options,
    isEditUser: currentUser !== null,
    handleOpenUserModal,
    handleSubmit,
    handleSubmitUser,
    handleRemoveUser,
    handleOpenRemoveModal,
    handleCollapse,
    toDisableModal,
    toDisableModalUser,
  };
}
